// App wiring of the offline outbox: singletons, triggers, zustand store and the public imperative API.
import type { QueryClient } from '@tanstack/react-query';
import { create } from 'zustand';
import { parseJwt, userManager } from '../auth';
import { useAuthStore } from '../store';
import { applyOverlayToCache, installCacheOverlay, invalidateSyncedQueries } from './cacheOverlay';
import { Outbox, createIdbOutboxStorage } from './outbox';
import { SyncEngine, type FlushOptions, type FlushSummary } from './syncEngine';
import { apiSyncTransport } from './transport';
import type { AttendanceOutboxEntry, OfflineIdentity, OutboxEntry, SessionSummaryOutboxEntry } from './types';

export const SYNC_INTERVAL_MS = 30_000;
const KICK_DEBOUNCE_MS = 300;

// ---- Identity -------------------------------------------------------------------------------------------------

/** Current user (`sub`) + active role, synchronously from the auth store (impersonated user while impersonating). */
export function selectOfflineUserId(state: ReturnType<typeof useAuthStore.getState>): string | null {
  return (
    (state.impersonationToken ? parseJwt(state.impersonationToken)?.sub : null) ??
    state.userContext?.user?.id ??
    (state.token ? parseJwt(state.token)?.sub : null) ??
    null
  );
}

export function getOfflineIdentity(): OfflineIdentity | null {
  const state = useAuthStore.getState();
  const userId = selectOfflineUserId(state);
  return userId ? { userId, activeRole: state.activeRole } : null;
}

/** Like `getOfflineIdentity`, falling back to the oidc-client user (e.g. before the user context is loaded). */
export async function resolveOfflineIdentity(): Promise<OfflineIdentity | null> {
  const fromStore = getOfflineIdentity();
  if (fromStore) return fromStore;
  try {
    const user = await userManager.getUser();
    const sub = user?.profile?.sub;
    return sub ? { userId: sub, activeRole: useAuthStore.getState().activeRole } : null;
  } catch {
    return null;
  }
}

// ---- Store ----------------------------------------------------------------------------------------------------

/** Current user's outbox view (recomputed on every outbox / user change; stable reference otherwise). */
export interface OutboxView {
  userId: string | null;
  /** Current user's entries (pending + rejected), oldest first. */
  entries: OutboxEntry[];
  pending: OutboxEntry[];
  rejected: OutboxEntry[];
  pendingCount: number;
  rejectedCount: number;
  /** Queued attendance entries by enrollment id (for per-student badges). */
  attendanceByEnrollment: Record<string, AttendanceOutboxEntry>;
  /** Queued notes/topic/summarize entries by session id. */
  summaryBySession: Record<string, SessionSummaryOutboxEntry>;
  /** Entries were loaded from IndexedDB. */
  loaded: boolean;
  flushing: boolean;
  lastSyncAt: number | null;
}

export interface ConnectivityState {
  /** `browserOnline && serverReachable`. */
  isOnline: boolean;
  /** `navigator.onLine` (tracked via `online` / `offline` events). */
  browserOnline: boolean;
  /** False after a sync request got no response; true again after the next response or `online` event. */
  serverReachable: boolean;
  lastNetworkErrorAt: number | null;
}

export interface OutboxStoreState {
  /** All outbox entries on this device (all users). */
  entries: OutboxEntry[];
  loaded: boolean;
  flushing: boolean;
  lastSyncAt: number | null;
  view: OutboxView;
  connectivity: ConnectivityState;
}

function buildView(state: Pick<OutboxStoreState, 'entries' | 'loaded' | 'flushing' | 'lastSyncAt'>, userId: string | null): OutboxView {
  const entries = userId ? state.entries.filter((e) => e.userId === userId) : [];
  const attendanceByEnrollment: Record<string, AttendanceOutboxEntry> = {};
  const summaryBySession: Record<string, SessionSummaryOutboxEntry> = {};
  for (const entry of entries) {
    if (entry.kind === 'attendance') attendanceByEnrollment[entry.enrollmentId] = entry;
    else summaryBySession[entry.sessionId] = entry;
  }
  const pending = entries.filter((e) => e.state === 'pending');
  const rejected = entries.filter((e) => e.state === 'rejected');
  return {
    userId,
    entries,
    pending,
    rejected,
    pendingCount: pending.length,
    rejectedCount: rejected.length,
    attendanceByEnrollment,
    summaryBySession,
    loaded: state.loaded,
    flushing: state.flushing,
    lastSyncAt: state.lastSyncAt,
  };
}

const initialBrowserOnline = typeof navigator === 'undefined' ? true : navigator.onLine !== false;

export const useOutboxStore = create<OutboxStoreState>(() => ({
  entries: [],
  loaded: false,
  flushing: false,
  lastSyncAt: null,
  view: buildView({ entries: [], loaded: false, flushing: false, lastSyncAt: null }, null),
  connectivity: { isOnline: initialBrowserOnline, browserOnline: initialBrowserOnline, serverReachable: true, lastNetworkErrorAt: null },
}));

function updateOutboxState(patch: Partial<Pick<OutboxStoreState, 'entries' | 'loaded' | 'flushing' | 'lastSyncAt'>>) {
  const next = { ...useOutboxStore.getState(), ...patch };
  useOutboxStore.setState({ ...patch, view: buildView(next, selectOfflineUserId(useAuthStore.getState())) });
}

function updateConnectivity(patch: Partial<Omit<ConnectivityState, 'isOnline'>>) {
  const current = useOutboxStore.getState().connectivity;
  const next = { ...current, ...patch };
  next.isOnline = next.browserOnline && next.serverReachable;
  if (
    next.browserOnline !== current.browserOnline ||
    next.serverReachable !== current.serverReachable ||
    next.lastNetworkErrorAt !== current.lastNetworkErrorAt
  ) {
    useOutboxStore.setState({ connectivity: next });
  }
}

// Keep the per-user view in sync with login / logout / impersonation.
let viewUserId = selectOfflineUserId(useAuthStore.getState());
useAuthStore.subscribe((state) => {
  const userId = selectOfflineUserId(state);
  if (userId !== viewUserId) {
    viewUserId = userId;
    updateOutboxState({});
  }
});

if (typeof window !== 'undefined') {
  window.addEventListener('online', () => updateConnectivity({ browserOnline: true, serverReachable: true }));
  window.addEventListener('offline', () => updateConnectivity({ browserOnline: false }));
}

// ---- Singletons -----------------------------------------------------------------------------------------------

export const outbox = new Outbox(createIdbOutboxStorage());
outbox.subscribe((entries) => updateOutboxState({ entries }));

let queryClientRef: QueryClient | null = null;

export const syncEngine = new SyncEngine({
  outbox,
  transport: apiSyncTransport,
  getCurrentUserId: async () => (await resolveOfflineIdentity())?.userId ?? null,
  renewAuth: async () => {
    try {
      const user = await userManager.signinSilent();
      return !!user?.access_token;
    } catch {
      return false;
    }
  },
  isOnline: () => typeof navigator === 'undefined' || navigator.onLine !== false,
  onNetworkStatus: (reachable) =>
    updateConnectivity(reachable ? { serverReachable: true } : { serverReachable: false, lastNetworkErrorAt: Date.now() }),
  onFlushStateChange: (flushing) => updateOutboxState(flushing ? { flushing } : { flushing, lastSyncAt: Date.now() }),
  onSynced: (schoolIds) => {
    const qc = queryClientRef;
    if (!qc) return;
    schoolIds.forEach((schoolId) => invalidateSyncedQueries(qc, schoolId));
  },
});

// ---- Public imperative API ------------------------------------------------------------------------------------

/** Flushes the current user's pending entries now (manual retry, after login). Ignores backoff by default. */
export async function flushOutbox(options: FlushOptions = { force: true }): Promise<FlushSummary> {
  await outbox.load();
  return syncEngine.flush(options);
}

let kickTimer: ReturnType<typeof setTimeout> | null = null;

/** Debounced flush after a local write (a quick series of taps goes out as one bulk request). */
export function kickOutbox() {
  if (kickTimer) clearTimeout(kickTimer);
  kickTimer = setTimeout(() => {
    kickTimer = null;
    void flushOutbox({ force: false });
  }, KICK_DEBOUNCE_MS);
}

function currentUserId(): string | null {
  return getOfflineIdentity()?.userId ?? null;
}

/** Puts a (rejected or backed-off) entry of the current user back to pending and flushes. */
export function retryEntry(key: string): void {
  const userId = currentUserId();
  if (!userId) return;
  if (outbox.retry(userId, key)) {
    if (queryClientRef) applyOverlayToCache(queryClientRef, outbox.all());
    void flushOutbox({ force: true });
  }
}

/** Drops an entry of the current user without sending it; the cache is refreshed from the server. */
export function discardEntry(key: string): void {
  const userId = currentUserId();
  if (!userId) return;
  const removed = outbox.remove(userId, key);
  if (removed && queryClientRef) invalidateSyncedQueries(queryClientRef, removed.schoolId);
}

/** Retries all rejected entries of the current user. */
export function retryAllRejected(): void {
  const userId = currentUserId();
  if (!userId) return;
  outbox.forUser(userId).filter((e) => e.state === 'rejected').forEach((e) => outbox.retry(userId, e.key));
  void flushOutbox({ force: true });
}

/**
 * True when the user (default: current user) still has unsent (`pending`) entries on this device.
 * Use before logout to warn the user; entries are never wiped on logout.
 */
export async function hasPendingOutboxEntries(userId?: string | null): Promise<boolean> {
  await outbox.load();
  const id = userId === undefined ? (await resolveOfflineIdentity())?.userId ?? null : userId;
  if (!id) return false;
  return outbox.hasPending(id);
}

/** Pending / rejected entries of a user (default: current user). */
export async function getOutboxEntries(userId?: string | null): Promise<OutboxEntry[]> {
  await outbox.load();
  const id = userId === undefined ? (await resolveOfflineIdentity())?.userId ?? null : userId;
  return id ? outbox.forUser(id) : [];
}

/** Registered QueryClient (set by `startOfflineSync`). */
export function getOfflineQueryClient(): QueryClient | null {
  return queryClientRef;
}

let stopCurrent: (() => void) | null = null;

/**
 * Starts the offline outbox for the app: loads persisted entries, installs the optimistic cache overlay and
 * the sync triggers (app start, `online`, tab visible, 30 s interval while entries are pending, login / user
 * change). Call once at startup with the app's QueryClient. Returns a stop function (idempotent re-start safe).
 */
export function startOfflineSync(queryClient: QueryClient): () => void {
  stopCurrent?.();
  queryClientRef = queryClient;
  const cleanups: Array<() => void> = [];

  cleanups.push(installCacheOverlay(queryClient, () => outbox.all()));

  void outbox.load().then(() => {
    updateOutboxState({ loaded: true });
    applyOverlayToCache(queryClient, outbox.all());
    void syncEngine.flush({ force: true });
  });

  if (typeof window !== 'undefined') {
    const onOnline = () => {
      void flushOutbox({ force: true });
    };
    window.addEventListener('online', onOnline);
    cleanups.push(() => window.removeEventListener('online', onOnline));

    const onVisibility = () => {
      if (document.visibilityState === 'visible') void flushOutbox({ force: true });
    };
    document.addEventListener('visibilitychange', onVisibility);
    cleanups.push(() => document.removeEventListener('visibilitychange', onVisibility));

    const interval = setInterval(() => {
      const userId = currentUserId();
      if (userId && outbox.hasPending(userId)) void syncEngine.flush({ force: false });
    }, SYNC_INTERVAL_MS);
    cleanups.push(() => clearInterval(interval));
  }

  // After login / user switch: flush the entries this user left behind.
  let lastUserId = currentUserId();
  cleanups.push(
    useAuthStore.subscribe(() => {
      const userId = currentUserId();
      if (userId && userId !== lastUserId) void flushOutbox({ force: true });
      lastUserId = userId;
    })
  );

  const stop = () => {
    cleanups.forEach((fn) => fn());
    if (stopCurrent === stop) stopCurrent = null;
  };
  stopCurrent = stop;
  return stop;
}
